import React, { useLayoutEffect, useRef } from 'react';
import type { StatusCategory } from '@jira/platform__status-categories/src/status-categories.tsx';
import GlobeIcon from '@atlaskit/icon/core/globe';
import { token } from '@atlaskit/tokens';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { STATE_COLOURS, STATUS_HEIGHT } from './ellipsis.tsx';
import { NODE_TYPE } from './directionKeys.tsx';
import { type Point, ValidationFlag } from './toPortInfo.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import MoveArrow, { ARROW_RADIUS } from './MoveArrow.tsx';
import { truncateLabel } from './isEveryPropertyDefined.tsx';
import { useDraggableDataAttributes } from './useCanvasDragDropEvents.tsx';
import { useLayoutWidth } from './useLayoutWidth.tsx';

const STATUS_TEXT_OFFSET_Y = 3.25;
const STATUS_TEXT_HORIZONTAL_PADDING = 50;
const OUTLINE_PADDING = 3;
const DEFAULT_BORDER_WIDTH = 2;
const FOCUS_RING_WIDTH = 4;
const SELECTION_RING_RADIUS = 6;
const BORDER_RADIUS = 4;
const ERROR_RING_DASH_ARRAY = '6,3';
const ARROW_PADDING = OUTLINE_PADDING + FOCUS_RING_WIDTH + ARROW_RADIUS;
/**
 * Icon size "small" is 16px
 */
const GLOBE_ICON_SIZE = 16;
const GLOBE_ICON_GAP = 4;
const GLOBE_INDICATOR_WIDTH = GLOBE_ICON_SIZE + GLOBE_ICON_GAP;
/**
 * Used to vertically centre the icon with the status label.
 */
const GLOBE_TEXT_BASELINE_TO_OPTICAL_CENTER = 6;

export interface Props {
	id: string;
	label: string;
	position?: Point;
	category?: StatusCategory;
	flag?: ValidationFlag;
	isImmutable?: boolean;
	isSelected?: boolean;
	isTargeted?: boolean;
	isMoving?: boolean;
	isDragging?: boolean;
	showGlobalScopeIndicator: boolean;
	onSelect?: (item: ItemReference | undefined) => void;
	onHover?: (item: ItemReference | undefined) => void;
	onStatusResize?: (key: string, value: number) => void;
}

const statusCategoryThemes = {
	UNDEFINED: {
		border: token('color.background.accent.gray.subtler'),
		background: token('color.background.accent.gray.subtlest'),
		text: token('color.text.accent.gray.bolder'),
		icon: token('color.icon.accent.gray'),
	},
	TODO: {
		border: token('color.background.accent.gray.subtler'),
		background: token('color.background.accent.gray.subtlest'),
		text: token('color.text.accent.gray.bolder'),
		icon: token('color.icon.accent.gray'),
	},
	IN_PROGRESS: {
		border: token('color.background.accent.blue.subtler'),
		background: token('color.background.accent.blue.subtlest'),
		text: token('color.text.accent.blue.bolder'),
		icon: token('color.icon.accent.blue'),
	},
	DONE: {
		border: token('color.background.accent.green.subtler'),
		background: token('color.background.accent.green.subtlest'),
		text: token('color.text.accent.green.bolder'),
		icon: token('color.icon.accent.green'),
	},
} satisfies Record<
	StatusCategory,
	{ border: string; background: string; text: string; icon: string }
>;

const DiagramStatus = ({
	id,
	position = { x: 0, y: 0 },
	label,
	category = 'UNDEFINED',
	flag,
	isImmutable,
	isSelected = false,
	isTargeted = false,
	isMoving = false,
	showGlobalScopeIndicator = false,
	onStatusResize,
	onHover,
	onSelect,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const textRef = useRef<SVGTextElement>(null);
	const lastReportedWidthRef = useRef<number | undefined>();
	const labelWidth = useLayoutWidth(textRef, [label]);
	const width =
		labelWidth +
		STATUS_TEXT_HORIZONTAL_PADDING +
		(showGlobalScopeIndicator ? GLOBE_INDICATOR_WIDTH : 0);

	// Track last reported width to prevent infinite loops when Strict Mode is enabled
	// Only notify parent when width actually changes, not on every render
	useLayoutEffect(() => {
		if (width > STATUS_TEXT_HORIZONTAL_PADDING && lastReportedWidthRef.current !== width) {
			lastReportedWidthRef.current = width;
			onStatusResize?.(id, width);
		}
	}, [id, width, onStatusResize]);

	const dataAttributes = useDraggableDataAttributes('status', id);

	const onPointerEnter = () => onHover?.({ id, type: NODE_TYPE });
	const onPointerLeave = () => onHover?.(undefined);

	const onGroupClick = () => {
		if (!isImmutable) {
			onSelect?.({
				type: NODE_TYPE,
				id,
			});
		}
	};

	const truncatedLabel = truncateLabel(label);

	const hasError = flag === ValidationFlag.Error;
	const hasWarning = flag === ValidationFlag.Warn;
	const hasValidationFlag = hasError || hasWarning;

	const theme = statusCategoryThemes[category];

	const statusLeft = position.x;

	return (
		<>
			<g>
				{isTargeted && (
					<rect
						x={statusLeft - OUTLINE_PADDING - FOCUS_RING_WIDTH / 4}
						y={position.y - OUTLINE_PADDING - FOCUS_RING_WIDTH / 4}
						width={width + OUTLINE_PADDING * 2 + FOCUS_RING_WIDTH / 2}
						height={STATUS_HEIGHT + OUTLINE_PADDING * 2 + FOCUS_RING_WIDTH / 2}
						rx={SELECTION_RING_RADIUS}
						ry={SELECTION_RING_RADIUS}
						stroke={STATE_COLOURS.focused}
						fillOpacity="0"
						strokeWidth={FOCUS_RING_WIDTH}
					/>
				)}
				{isMoving && (
					<>
						<MoveArrow x={statusLeft + width / 2} y={position.y - ARROW_PADDING} direction="up" />
						<MoveArrow
							x={statusLeft + width / 2}
							y={position.y + STATUS_HEIGHT + ARROW_PADDING}
							direction="down"
						/>
						<MoveArrow
							x={statusLeft - ARROW_PADDING}
							y={position.y + STATUS_HEIGHT / 2}
							direction="left"
						/>
						<MoveArrow
							x={statusLeft + width + ARROW_PADDING}
							y={position.y + STATUS_HEIGHT / 2}
							direction="right"
						/>
					</>
				)}
				{hasValidationFlag && (
					<rect
						x={statusLeft - OUTLINE_PADDING}
						y={position.y - OUTLINE_PADDING}
						width={width + OUTLINE_PADDING * 2}
						height={STATUS_HEIGHT + OUTLINE_PADDING * 2}
						rx={SELECTION_RING_RADIUS}
						ry={SELECTION_RING_RADIUS}
						stroke={hasError ? STATE_COLOURS.error : STATE_COLOURS.warning}
						fillOpacity="0"
						strokeDasharray={!isSelected ? ERROR_RING_DASH_ARRAY : undefined}
						strokeWidth={DEFAULT_BORDER_WIDTH}
					/>
				)}
				{isSelected && !hasValidationFlag && (
					<rect
						x={statusLeft - OUTLINE_PADDING}
						y={position.y - OUTLINE_PADDING}
						width={width + OUTLINE_PADDING * 2}
						height={STATUS_HEIGHT + OUTLINE_PADDING * 2}
						rx={SELECTION_RING_RADIUS}
						ry={SELECTION_RING_RADIUS}
						stroke={STATE_COLOURS.selected}
						fillOpacity="0"
						strokeWidth={DEFAULT_BORDER_WIDTH}
					/>
				)}
				<g
					{...dataAttributes}
					cursor={!isImmutable ? 'move' : undefined}
					onPointerEnter={onPointerEnter}
					onPointerLeave={onPointerLeave}
					onClick={onGroupClick}
				>
					{/* bg */}
					<rect
						x={statusLeft}
						y={position.y}
						width={width}
						height={STATUS_HEIGHT}
						rx={BORDER_RADIUS}
						ry={BORDER_RADIUS}
						fill={theme.background}
						stroke={theme.border}
						strokeWidth={DEFAULT_BORDER_WIDTH}
					/>
					{/* label */}
					<text
						x={statusLeft + width / 2 - (showGlobalScopeIndicator ? GLOBE_INDICATOR_WIDTH / 2 : 0)}
						y={position.y + STATUS_HEIGHT / 2 + STATUS_TEXT_OFFSET_Y}
						fontSize="11px"
						stroke="none"
						fill={theme.text}
						textAnchor="middle"
						fontWeight="bold"
						strokeWidth="1"
					>
						<tspan ref={textRef}>{truncatedLabel.toLocaleUpperCase()}</tspan>
					</text>
					{showGlobalScopeIndicator && (
						<foreignObject
							x={
								statusLeft + width / 2 + labelWidth / 2 - GLOBE_INDICATOR_WIDTH / 2 + GLOBE_ICON_GAP
							}
							y={
								position.y +
								STATUS_HEIGHT / 2 +
								STATUS_TEXT_OFFSET_Y -
								GLOBE_TEXT_BASELINE_TO_OPTICAL_CENTER -
								GLOBE_ICON_SIZE / 2
							}
							width={GLOBE_ICON_SIZE}
							height={GLOBE_ICON_SIZE}
							data-testid="accessible-workflow-diagram.global-indicator"
						>
							<GlobeIcon
								label={formatMessage(globalStatusIconLabel)}
								color={theme.icon}
								size="small"
							/>
						</foreignObject>
					)}
				</g>
			</g>
		</>
	);
};

const globalStatusIconLabel = defineMessage({
	id: 'accessible-workflow-diagram.diagram-status.global-status-icon-label',
	defaultMessage: 'Global status',
	description:
		'Accessibility label for the globe icon shown next to a globally scoped status in the workflow diagram.',
});

export default DiagramStatus;
